/**
 * ActiveRouteIndicator Component
 *
 * Display-only indicator showing the currently active route in the breadcrumb area.
 * Visible on all tabs when a route is active for the selected campaign.
 *
 * Features:
 * - Amber/brand colored "Active Route:" label (matches DMS desktop)
 * - Route name display with truncation
 * - Tooltip with route details (stops, distance)
 * - Hidden when no active route
 */

import React from 'react';
import { Route as RouteIcon } from 'lucide-react';
import { useCampaignStore } from '../../../features/campaigns/store/campaignStore';
import { useRouteStore } from '../../../features/routes/store/routeStore';

export interface ActiveRouteIndicatorProps {
  /** Additional CSS classes */
  className?: string;
  /** Show separator before indicator (default: true for inline use) */
  showSeparator?: boolean;
}

export const ActiveRouteIndicator: React.FC<ActiveRouteIndicatorProps> = ({
  className = '',
  showSeparator = true,
}) => {
  const { selectedCampaignId } = useCampaignStore();
  const { activeRoutesByCampaign, routeHistory } = useRouteStore();

  // Get active route for current campaign
  const activeRouteId = selectedCampaignId
    ? activeRoutesByCampaign[selectedCampaignId]
    : null;

  // Safely handle routeHistory (ensure it's an array)
  const routeHistoryArray = Array.isArray(routeHistory) ? routeHistory : [];
  const activeRoute = routeHistoryArray.find(r => r.route_id === activeRouteId);

  // Don't render if no active route
  if (!activeRoute) {
    return null;
  }

  // Format tooltip with route details
  const tooltipText = `${activeRoute.total_stops} stops | ${Math.round(activeRoute.total_distance_miles)} mi`;

  return (
    <div
      className={`flex items-center gap-1.5 text-xs ${className}`}
      title={tooltipText}
    >
      {/* Separator - only for inline use */}
      {showSeparator && <span className="text-dark-muted mx-1">|</span>}

      {/* Route icon */}
      <RouteIcon className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />

      {/* Amber label */}
      <span className="text-amber-500 font-medium whitespace-nowrap">
        Active Route:
      </span>

      {/* Route name */}
      <span className="text-dark-text truncate max-w-[150px] md:max-w-[200px]">
        {activeRoute.route_name || 'Unnamed Route'}
      </span>

      {/* Route stats (visible on larger screens) */}
      <span className="hidden sm:inline text-dark-muted whitespace-nowrap">
        ({activeRoute.total_stops} stops)
      </span>
    </div>
  );
};

export default ActiveRouteIndicator;
